/**
 * Resolves a `PointingTag` into an absolute viewport coordinate where
 * the bird should land. Handles both ref-based lookup and pixel-coord
 * fallback. If the element is outside the viewport, scrolls it into
 * view first (smoothly) so the bird flies to a visible target.
 */

import type { PageContext, PointingTag } from "../types";

export interface ResolvedPointingTarget {
  /** Final viewport coordinates the bird should fly to (CSS pixels). */
  viewportX: number;
  viewportY: number;
  /** The resolved element, if ref-based and found. */
  element: HTMLElement | null;
  /**
   * Re-evaluate the target's current viewport position. Used during
   * the pointing hold so the bird tracks the element through page
   * scrolls or layout shifts. Returns null when the element has been
   * removed from the DOM, or when the target was pixel-coord-based
   * (no DOM anchor to track).
   */
  refreshPosition: () => { viewportX: number; viewportY: number } | null;
}

export async function resolvePointingTarget(
  tag: PointingTag,
  pageContextAtCapture: PageContext,
): Promise<ResolvedPointingTarget | null> {
  if (tag.ref) {
    const resolved = await resolveByRef(tag.ref);
    if (resolved) return resolved;
    // If ref lookup failed entirely, abandon pointing rather than guess.
    return null;
  }

  if (tag.pixelCoords) {
    return resolveByPixelCoords(tag.pixelCoords, pageContextAtCapture);
  }

  return null;
}

async function resolveByRef(
  ref: string,
): Promise<ResolvedPointingTarget | null> {
  const element = document.querySelector<HTMLElement>(
    `[data-koko-ref="${cssEscape(ref)}"]`,
  );
  if (!element) return null;

  await scrollIntoViewIfNeeded(element);

  // Compute the bird's landing point from a fresh rect every time
  // refreshPosition is called. Returning the same closure for both the
  // initial read and the hold-time tracking keeps the "aim at top-right
  // of element" logic in exactly one place.
  const refreshPosition = (): { viewportX: number; viewportY: number } | null => {
    if (!element.isConnected) return null;
    const rect = element.getBoundingClientRect();
    return {
      viewportX: rect.right - Math.min(rect.width * 0.2, 18),
      viewportY: rect.top + rect.height / 2,
    };
  };

  const initial = refreshPosition();
  if (!initial) return null;
  return {
    viewportX: initial.viewportX,
    viewportY: initial.viewportY,
    element,
    refreshPosition,
  };
}

function resolveByPixelCoords(
  pixelCoords: { x: number; y: number },
  pageContextAtCapture: PageContext,
): ResolvedPointingTarget {
  // Screenshot was captured at `viewportWidth x viewportHeight` CSS pixels.
  // Map Claude's pixel coords (in screenshot space) back to current viewport space.
  const currentWidth = window.innerWidth;
  const currentHeight = window.innerHeight;
  const scaleX = currentWidth / pageContextAtCapture.viewportWidth;
  const scaleY = currentHeight / pageContextAtCapture.viewportHeight;
  const viewportX = pixelCoords.x * scaleX;
  const viewportY = pixelCoords.y * scaleY;
  return {
    viewportX,
    viewportY,
    element: null,
    // Pixel coords have no DOM anchor — there's nothing to track on
    // scroll. Return null so the FlightController falls back to
    // holding the bird in place.
    refreshPosition: () => null,
  };
}

async function scrollIntoViewIfNeeded(element: HTMLElement): Promise<void> {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const padding = 80;
  const isFullyVisible =
    rect.top >= padding && rect.bottom <= viewportHeight - padding;
  if (isFullyVisible) return;

  element.scrollIntoView({ behavior: "smooth", block: "center" });
  // Wait for the smooth scroll to settle. There's no native "scrollend"
  // event everywhere yet, so we poll the rect until it stabilizes or we
  // hit a reasonable timeout.
  await waitForScrollToSettle();
}

async function waitForScrollToSettle(maxMs = 900): Promise<void> {
  const start = performance.now();
  let previousScrollY = window.scrollY;
  let stableTicks = 0;
  while (performance.now() - start < maxMs) {
    await new Promise((resolve) => window.setTimeout(resolve, 60));
    if (Math.abs(window.scrollY - previousScrollY) < 0.5) {
      stableTicks += 1;
      if (stableTicks >= 2) return;
    } else {
      stableTicks = 0;
      previousScrollY = window.scrollY;
    }
  }
}

/** Minimal CSS.escape polyfill so we work in older Safari too. */
function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, (character) => `\\${character}`);
}
